/** Sweet-state hooks for the spaces store. */

import {
	createActionsHook,
	createHook,
	createStateHook,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type HookStateFunction,
} from 'react-sweet-state';

import { spaceStore } from './store';
import type { SpaceStoreActions } from './store';
import type { Space, SpaceConversationsResponse, SpaceResponse, SpaceStoreState } from './types';

type Actions = BoundActions<SpaceStoreState, SpaceStoreActions>;

/** [state, actions] — mirrors `useStagingArea` etc. Compat shape. */
export const useSpaceStoreCombined: HookFunction<SpaceStoreState, Actions> = createHook(spaceStore);

/** State only — replaces the React-context `useSpaceStore()`. */
export const useSpaceStoreState: HookStateFunction<SpaceStoreState> = createStateHook(spaceStore);

/** Actions only — replaces the React-context `useSpaceStoreActions()`. */
export const useSpaceStoreActions: HookActionsFunction<Actions> = createActionsHook(spaceStore);

// --- Selectors -----------------------------------------------------------

/** The list of spaces (side-nav). Stable reference per spaces-array identity. */
export const useSpaces: HookStateFunction<Space[]> = createStateHook(spaceStore, {
	selector: (state: SpaceStoreState) => state.spaces,
});

/** True while the list endpoint is in flight. */
export const useSpacesLoading: HookStateFunction<boolean> = createStateHook(spaceStore, {
	selector: (state: SpaceStoreState) => state.isListLoading,
});

/** Error from the most recent list endpoint call, if any. */
export const useSpacesError: HookStateFunction<Error | null> = createStateHook(spaceStore, {
	selector: (state: SpaceStoreState) => state.listError,
});

/** Whether a successful list fetch has been merged in at least once. */
export const useHasFetchedSpaces: HookStateFunction<boolean> = createStateHook(spaceStore, {
	selector: (state: SpaceStoreState) => state.hasFetchedList,
});

/** Aggregated conversationIds across all spaces. Stable array reference — pre-computed in store state. */
export const useSpaceConversationIds: HookStateFunction<string[]> = createStateHook(spaceStore, {
	selector: (state: SpaceStoreState) => state.conversationIds,
});

// --- Per-id detail selectors --------------------------------------------

/**
 * Per-id detail-shape selector. Returns `undefined` until the detail has been
 * fetched for the given id.
 */
export const useSpaceById: HookStateFunction<SpaceResponse | undefined, string> = createStateHook(
	spaceStore,
	{
		selector: (state: SpaceStoreState, spaceId: string) => state.spaceDetails.get(spaceId),
	},
);

export const useSpaceDetailLoading: HookStateFunction<boolean, string> = createStateHook(
	spaceStore,
	{
		selector: (state: SpaceStoreState, spaceId: string) =>
			state.detailLoading.get(spaceId) ?? false,
	},
);

export const useSpaceDetailError: HookStateFunction<Error | null, string> = createStateHook(
	spaceStore,
	{
		selector: (state: SpaceStoreState, spaceId: string) => state.detailErrors.get(spaceId) ?? null,
	},
);

// --- Per-id conversations selectors -------------------------------------

export const useSpaceConversationsPage: HookStateFunction<
	SpaceConversationsResponse | null,
	string
> = createStateHook(spaceStore, {
	selector: (state: SpaceStoreState, spaceId: string) =>
		state.spaceConversations.get(spaceId) ?? null,
});

export const useSpaceConversationsLoading: HookStateFunction<boolean, string> = createStateHook(
	spaceStore,
	{
		selector: (state: SpaceStoreState, spaceId: string) =>
			state.conversationsLoading.get(spaceId) ?? false,
	},
);

export const useSpaceConversationsError: HookStateFunction<Error | null, string> = createStateHook(
	spaceStore,
	{
		selector: (state: SpaceStoreState, spaceId: string) =>
			state.conversationsErrors.get(spaceId) ?? null,
	},
);
